package de.xtl.retroguide.protocol

import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

const val MIN_ROTI = 1
const val MAX_ROTI = 5

private const val CATEGORY_SEPARATOR = ':'

@Serializable
enum class AnswerCategory(val emoji: String, val exportLabel: String) {
    WIND("🌬️", "Wind"),
    ANCHOR("⚓", "Anker"),
    ROCK("🪨", "Felsen"),
    ISLAND("🏝️", "Insel"),
    MAD("😠", "Mad"),
    SAD("😢", "Sad"),
    GLAD("😊", "Glad"),
    START("▶️", "Start"),
    STOP("⏹️", "Stop"),
    CONTINUE("🔁", "Continue"),
    MORE("➕", "More"),
    LESS("➖", "Less"),
    LIKED("💚", "Liked"),
    LEARNED("💡", "Learned"),
    LACKED("🕳️", "Lacked"),
    LONGED_FOR("🌠", "Longed for"),
    FIRE("🔥", "Feuer"),
    SANDBAG("🎒", "Sandsäcke"),
    SUN("☀️", "Sonne"),
    STORM("⛈️", "Sturm"),
    ENGINE("⚙️", "Motor"),
    PARACHUTE("🪂", "Fallschirm"),
    ABYSS("🕳️", "Abgrund"),
    BRIDGE("🌉", "Brücke"),
    KEEP("📌", "Keep"),
    ADD("✨", "Add"),
    DROP("🗑️", "Drop"),
    IMPROVE("🔧", "Improve"),
}

@Serializable
enum class RadarDimension(val exportLabel: String) {
    FUN("Spaß"),
    SPEED("Tempo"),
    QUALITY("Qualität"),
    COLLABORATION("Zusammenarbeit"),
    LEARNING("Lernen"),
    FOCUS("Fokus"),
}

val QuestionWidget.categories: List<AnswerCategory>
    get() = when (this) {
        QuestionWidget.SAILBOAT -> listOf(
            AnswerCategory.WIND,
            AnswerCategory.ANCHOR,
            AnswerCategory.ROCK,
            AnswerCategory.ISLAND,
        )
        QuestionWidget.MAD_SAD_GLAD -> listOf(AnswerCategory.MAD, AnswerCategory.SAD, AnswerCategory.GLAD)
        QuestionWidget.STARFISH -> listOf(
            AnswerCategory.START,
            AnswerCategory.STOP,
            AnswerCategory.CONTINUE,
            AnswerCategory.MORE,
            AnswerCategory.LESS,
        )
        QuestionWidget.FOUR_L -> listOf(
            AnswerCategory.LIKED,
            AnswerCategory.LEARNED,
            AnswerCategory.LACKED,
            AnswerCategory.LONGED_FOR,
        )
        QuestionWidget.HOT_AIR_BALLOON -> listOf(
            AnswerCategory.FIRE,
            AnswerCategory.SANDBAG,
            AnswerCategory.SUN,
            AnswerCategory.STORM,
        )
        QuestionWidget.SPEEDCAR -> listOf(
            AnswerCategory.ENGINE,
            AnswerCategory.PARACHUTE,
            AnswerCategory.ABYSS,
            AnswerCategory.BRIDGE,
        )
        QuestionWidget.KALM -> listOf(AnswerCategory.KEEP, AnswerCategory.ADD, AnswerCategory.LESS, AnswerCategory.MORE)
        QuestionWidget.DAKI -> listOf(AnswerCategory.DROP, AnswerCategory.ADD, AnswerCategory.KEEP, AnswerCategory.IMPROVE)
        QuestionWidget.TEXT, QuestionWidget.BATTERY, QuestionWidget.WEATHER, QuestionWidget.ROTI,
        QuestionWidget.TEAM_RADAR, QuestionWidget.DOT_VOTING, QuestionWidget.ACTION_ITEMS -> emptyList()
    }

val QuestionWidget.maxAnswersPerParticipant: Int
    get() = if (this == QuestionWidget.TEXT || this == QuestionWidget.ACTION_ITEMS || categories.isNotEmpty()) {
        MAX_ANSWERS_PER_QUESTION
    } else {
        1
    }

/** Free-text and category questions can be clustered and used as dot-voting source. */
val QuestionWidget.collectsTopics: Boolean
    get() = this == QuestionWidget.TEXT || categories.isNotEmpty()

data class CategorizedAnswer(val category: AnswerCategory, val text: String) {
    fun encode() = "${category.name}$CATEGORY_SEPARATOR$text"
}

fun String.toCategorizedAnswer(): CategorizedAnswer? {
    val separator = indexOf(CATEGORY_SEPARATOR)
    if (separator < 0) return null
    val category = AnswerCategory.entries.firstOrNull { it.name == substring(0, separator) } ?: return null
    return CategorizedAnswer(category, substring(separator + 1))
}

fun String.toRotiScore(): Int? = singleOrNull()?.toScore()

private fun Char.toScore(): Int? = takeIf { it in '1'..'5' }?.digitToInt()

data class RadarAnswer(val scores: Map<RadarDimension, Int>) {
    val isComplete: Boolean
        get() = scores.keys.containsAll(RadarDimension.entries)

    fun with(dimension: RadarDimension, score: Int) = RadarAnswer(scores + (dimension to score))

    fun encode() = RadarDimension.entries
        .mapNotNull { dimension -> scores[dimension]?.let { "${dimension.name}$RADAR_ASSIGNMENT$it" } }
        .joinToString(RADAR_SEPARATOR.toString())
}

/** Parses an encoded radar answer; an empty string is an empty (draft) answer. */
fun String.toRadarAnswer(): RadarAnswer? {
    if (isEmpty()) return RadarAnswer(emptyMap())
    val scores = mutableMapOf<RadarDimension, Int>()
    split(RADAR_SEPARATOR).forEach { entry ->
        val name = entry.substringBefore(RADAR_ASSIGNMENT, missingDelimiterValue = "")
        val dimension = RadarDimension.entries.firstOrNull { it.name == name } ?: return null
        val score = entry.substringAfter(RADAR_ASSIGNMENT).singleOrNull()?.toScore() ?: return null
        if (scores.put(dimension, score) != null) return null
    }
    return RadarAnswer(scores)
}

data class DotVote(val votes: Map<String, Int>) {
    val total: Int
        get() = votes.values.sum()

    fun with(optionId: String, count: Int) = DotVote(if (count <= 0) votes - optionId else votes + (optionId to count))

    fun encode() = votes.entries.joinToString(RADAR_SEPARATOR.toString()) { (id, count) -> "$id$RADAR_ASSIGNMENT$count" }

    fun isValidFor(optionIds: Set<String>, budget: Int) = total in 1..budget && optionIds.containsAll(votes.keys)
}

/** Parses an encoded dot vote; an empty string is an empty (draft) vote. */
fun String.toDotVote(): DotVote? {
    if (isEmpty()) return DotVote(emptyMap())
    val votes = linkedMapOf<String, Int>()
    split(RADAR_SEPARATOR).forEach { entry ->
        val id = entry.substringBefore(RADAR_ASSIGNMENT, missingDelimiterValue = "")
        val count = entry.substringAfter(RADAR_ASSIGNMENT)
        if (id.isEmpty() || count.isEmpty() || !count.all { it in '0'..'9' }) return null
        val value = count.takeIf { it.length <= 2 }?.toInt()?.takeIf { it in 1..MAX_VOTES_PER_PARTICIPANT } ?: return null
        if (votes.put(id, value) != null) return null
    }
    return DotVote(votes)
}

@Serializable
data class ActionItem(val what: String, val who: String = "", val due: String = "") {
    fun encode() = actionItemJson.encodeToString(serializer(), this)

    val isValid: Boolean
        get() = what.isNotBlank() && what.length <= MAX_ACTION_WHAT_LENGTH &&
            who.length <= MAX_ACTION_WHO_LENGTH && due.length <= MAX_ACTION_DUE_LENGTH
}

fun String.toActionItem(): ActionItem? = runCatching { actionItemJson.decodeFromString(ActionItem.serializer(), this) }.getOrNull()

const val MAX_ACTION_WHAT_LENGTH = 500
const val MAX_ACTION_WHO_LENGTH = 60
const val MAX_ACTION_DUE_LENGTH = 40

private val actionItemJson = Json { ignoreUnknownKeys = true }

private const val RADAR_SEPARATOR = ';'
private const val RADAR_ASSIGNMENT = '='

/** Expects already trimmed content. */
fun String.isValidAnswerFor(widget: QuestionWidget): Boolean = when (widget) {
    QuestionWidget.TEXT -> length in 1..MAX_ANSWER_LENGTH
    QuestionWidget.BATTERY -> BatteryLevel.entries.any { it.name == this }
    QuestionWidget.WEATHER -> WeatherMood.entries.any { it.name == this }
    QuestionWidget.ROTI -> toRotiScore() != null
    QuestionWidget.TEAM_RADAR -> toRadarAnswer()?.isComplete == true
    QuestionWidget.DOT_VOTING -> (toDotVote()?.total ?: 0) >= 1
    QuestionWidget.ACTION_ITEMS -> length <= MAX_ANSWER_LENGTH && toActionItem()?.isValid == true
    QuestionWidget.SAILBOAT, QuestionWidget.MAD_SAD_GLAD, QuestionWidget.STARFISH, QuestionWidget.FOUR_L,
    QuestionWidget.HOT_AIR_BALLOON, QuestionWidget.SPEEDCAR, QuestionWidget.KALM, QuestionWidget.DAKI ->
        toCategorizedAnswer()?.let {
            it.category in widget.categories && it.text.isNotBlank() && it.text.length <= MAX_ANSWER_LENGTH
        } == true
}
